import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { DonutChartComponent, DonutChartData } from '../../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { FilterService } from '../../../../../../_shared/services';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { MaintenanceCostByAssetRank } from '../../../models/maintenance-dashboard-request.model';
import { MaintenanceCostByAssetResponse } from '../../../models/maintenance-dashboard-response.model';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { Subject } from 'rxjs';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoPipe , TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-top-assets-cost',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, NgbDropdownModule, DonutChartComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './top-assets-cost.component.html',
  styleUrl: './top-assets-cost.component.scss'
})
export class TopAssetsCostComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private authService = inject(AuthService);

  title: string = this.transloco.translate('maintenanceDashboard.charts.topAssetsCost');
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  data: DonutChartData[] = [];
  filterLabel: string = 'Best';
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  private readonly colors = {
    best: ['#316D28', '#3B8A2F', '#50AD41', '#72C464', '#A3DC99'],
    worst: ['#E46F2C', '#FF7F00', '#FF9500', '#FFB132', '#FFE5A5']
  };

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadAssetCostData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    // Only load data if filter is initialized
    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadAssetCostData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadAssetCostData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const category = this.filterLabel.toLowerCase() as 'best' | 'worst';
      const [assetResponse, kpiResponse] = await Promise.all([
        this.dashboardService.getMaintenanceCostByAsset({
          ...request,
          ranking: category === 'best' ? MaintenanceCostByAssetRank.Best : MaintenanceCostByAssetRank.Worst
        }, this.cancelSubject),
        this.dashboardService.getMaintenanceCostByAssetKpi({
          ...request,
          previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
          previousTimeTo: this.widgetFilter.previousCycleEndDate!
        }, this.cancelSubject)
      ]);

      const kpi = kpiResponse.data;
      this.processAssetData(assetResponse.data ?? [], category);
      this.totalValue = kpi?.totalValue ?? 0;
      this.percentageChange = kpi?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private processAssetData(assets: MaintenanceCostByAssetResponse[], category: 'best' | 'worst'): void {
    if (!assets || assets.length === 0) {
      this.data = [];
      return;
    }

    // Convert asset data to donut chart format
    const colors = this.colors[category];

    this.data = assets.map((asset, index) => ({
      name: asset.assetName,
      value: asset.totalCost,
      color: colors[index % colors.length]
    }));
  }

  formatValue(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  async onFilterChange(filter: string): Promise<void> {
    this.filterLabel = filter;
    await this.loadAssetCostData();
  }

  hasData(): boolean {
    if (!this.data || this.data.length === 0) return false;

    return this.data.some(item => {
      const value = Number(item.value) || 0;
      return value > 0;
    });
  }
}